/**
 * Prefetch utility for coin packages
 * 
 * This module provides a simple prefetch mechanism for the Get Coins modal/page.
 * When users hover over the "Get Coins" button, we prefetch the packages data
 * so the modal opens faster.
 * 
 * Uses a simple in-memory cache with short TTL to avoid redundant requests
 * while ensuring data freshness.
 */

import { apiClient } from './api-client';

// Simple in-memory cache
let cachedData: any = null;
let cacheTimestamp: number = 0;
let prefetchPromise: Promise<any> | null = null;

// Cache TTL in milliseconds (10 seconds - short since offers need to be fresh)
const CACHE_TTL = 10_000;

/**
 * Prefetch coin packages data
 * 
 * Call this on hover/mouseenter to warm up the cache before the modal opens.
 * Uses request deduplication to avoid multiple concurrent requests.
 * 
 * @returns Promise that resolves when prefetch completes (can be ignored)
 */
export async function prefetchPackages(): Promise<void> {
  const now = Date.now();
  
  // If we have fresh cached data, no need to prefetch
  if (cachedData && (now - cacheTimestamp) < CACHE_TTL) {
    return;
  }
  
  // If a prefetch is already in progress, wait for it
  if (prefetchPromise) {
    await prefetchPromise;
    return;
  }
  
  // Start a new prefetch
  prefetchPromise = apiClient.get('/coins/packages')
    .then((data) => {
      cachedData = data;
      cacheTimestamp = Date.now();
      prefetchPromise = null;
    })
    .catch((error) => {
      // Silently fail - prefetch is best-effort
      console.debug('[prefetchPackages] Prefetch failed:', error);
      prefetchPromise = null;
    });
  
  await prefetchPromise;
}

/**
 * Get cached packages data if available and fresh
 * 
 * @returns Cached data or null if cache is stale/empty
 */
export function getCachedPackages(): any | null {
  const now = Date.now();
  if (cachedData && (now - cacheTimestamp) < CACHE_TTL) {
    return cachedData;
  }
  return null;
}

/**
 * Clear the packages cache
 * Call this after a purchase or when data might be stale
 */
export function clearPackagesCache(): void {
  cachedData = null;
  cacheTimestamp = 0;
}
